import React, { FC } from 'react';
import { Box, BoxProps } from '@mui/material';
import './loading-layout.css';

export const DEFAULT_LOADING_TEXT = 'Shuffling the deck…';

const DEALT_CARDS: { rank: string; suit: string; red: boolean }[] = [
  { rank: 'A', suit: '♠', red: false },
  { rank: 'K', suit: '♥', red: true },
  { rank: 'Q', suit: '♣', red: false },
  { rank: 'J', suit: '♦', red: true },
];

export interface LoadingProps {
  /** What is happening right now, e.g. "Finding your table…". */
  text?: string;
  /** Optional secondary line under the status. */
  hint?: string;
  /**
   * Render inside the page flow instead of covering the screen. Defaults to
   * `true` whenever a `height` is passed (legacy `height="auto"` callers).
   */
  inline?: boolean;
  /** Hide the "Bhabi" wordmark (useful for small inline states). */
  hideBrand?: boolean;
}

/** Four cards dealt onto a felt pad — purely decorative. */
export const DealingCards: FC = () => (
  <div className="bh-loading__table" aria-hidden="true" data-testid="bh-deal">
    {DEALT_CARDS.map(card => (
      <span
        key={card.rank}
        className="bh-loading__card"
        data-red={card.red ? 'true' : 'false'}
      >
        {card.suit}
      </span>
    ))}
  </div>
);

/**
 * Shared loading state for account, auth and router fallbacks. Full screen by
 * default; inline when `inline` (or a legacy `height`) is given.
 */
export const LoadingLayout: FC<LoadingProps & BoxProps> = ({
  text,
  hint,
  inline,
  hideBrand,
  className = '',
  ...props
}) => {
  const isInline = inline ?? props.height !== undefined;
  const status = text?.trim() ? text : DEFAULT_LOADING_TEXT;

  return (
    <Box
      role="status"
      aria-live="polite"
      {...props}
      className={[
        'bh-loading',
        isInline ? 'bh-loading--inline' : 'bh-loading--screen',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <DealingCards />
      {!hideBrand && !isInline && (
        <p className="bh-loading__brand" aria-hidden="true">
          Bhabi <span>♠</span>
        </p>
      )}
      <p className="bh-loading__status">{status}</p>
      {hint && <p className="bh-loading__hint">{hint}</p>}
    </Box>
  );
};

/**
 * Legacy compact variant (no full-screen cover). Kept for API compatibility.
 */
export const NormalBackgroundLoadingLayout: FC<{
  normalLayout?: boolean;
  text?: string;
}> = ({ text }) => <LoadingLayout inline hideBrand text={text} />;
